import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";

import AnchoredPopover from "./AnchoredPopover";
import { formatDateDMY, todayDate } from "./data";
import { CalendarIcon, Chevron } from "./scheduleIcons";

/**
 * "Past schedules ▾" — the header control that moves the section between
 * days. Opens a popover listing the days that have a schedule, newest
 * first, ten to a page, with a "Showing 1–10 of 42" line and a numbered
 * pager (‹ 1 2 3 4 5 ›) underneath. Picking a row moves the section to
 * that day.
 *
 * The rows are the section's shared five-year fetch (useFilledDays), so
 * pages are sliced locally — no request per page, and the list is always
 * in step with the calendar. It reopens on page 1 each time.
 *
 * "Back to today" shows while a past day is selected: today is only in the
 * list once it has a schedule, and there has to be a way home.
 *
 * `disabled` is for the nothing-on-record placeholder, where there is
 * nothing to move to; the button still renders so the header reads the
 * same in every state.
 */

export const PAGE_SIZE = 10;

// Page numbers to render: all of them when few, otherwise a window around
// the current page with the first and last always present and "…" for
// the gaps. Returns numbers and the string "…".
export const pagerItems = (page, pages) => {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const items = [1];
  const lo = Math.max(2, page - 1);
  const hi = Math.min(pages - 1, page + 1);
  if (lo > 2) items.push("…");
  for (let n = lo; n <= hi; n += 1) items.push(n);
  if (hi < pages - 1) items.push("…");
  items.push(pages);
  return items;
};

const PastSchedulesMenu = ({ days, status, onRetry, date, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const wrapRef = useRef(null);
  const today = todayDate();
  const all = days || [];
  const total = all.length;
  const items = all.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = () => {
    setOpen((o) => {
      if (!o) setPage(1);
      return !o;
    });
  };
  const close = useCallback(() => setOpen(false), []);

  const pick = (key) => {
    setOpen(false);
    if (!key || key === date) return;
    onSelect(key);
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  return (
    <div className="ds6-past-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ds6-past${open ? " is-open" : ""}`}
        disabled={disabled}
        title={disabled ? "No past schedules yet" : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <CalendarIcon /> Past schedules <Chevron dir="down" />
      </button>

      <AnchoredPopover
        open={open}
        anchorRef={wrapRef}
        onClose={close}
        className={`ds6-days${status === "loading" ? " is-loading" : ""}`}
        role="dialog"
        aria-label="Past schedules"
      >
          {date !== today && (
            <button type="button" className="ds6-days-today" onClick={() => pick(today)}>
              <Chevron dir="left" /> Back to today
            </button>
          )}

          {status === "error" ? (
            <p className="ds6-days-hint">
              Couldn&rsquo;t load past schedules.{" "}
              <button type="button" className="ds5-link" onClick={onRetry}>
                Retry
              </button>
            </p>
          ) : items.length === 0 && status !== "loading" ? (
            <p className="ds6-days-hint">No days captured yet.</p>
          ) : (
            <ul className="ds6-days-list">
              {items.map((d) => {
                const active = d.date === date;
                return (
                  <li key={d.date}>
                    <button
                      type="button"
                      className={`ds6-day${active ? " is-active" : ""}`}
                      onClick={() => pick(d.date)}
                      aria-current={active ? "date" : undefined}
                    >
                      {formatDateDMY(d.date)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {total > 0 && (
            <footer className="ds6-days-foot">
              <p className="ds6-days-showing">
                Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
              </p>
              {pages > 1 && (
                <nav className="ds6-pager" aria-label="Pages">
                  <button
                    type="button"
                    className="ds6-pager-nav"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label="Previous page"
                  >
                    <Chevron dir="left" />
                  </button>
                  {pagerItems(page, pages).map((n, i) =>
                    n === "…" ? (
                      // eslint-disable-next-line react/no-array-index-key
                      <span className="ds6-pager-gap" key={`gap-${i}`} aria-hidden="true">…</span>
                    ) : (
                      <button
                        type="button"
                        className={`ds6-pager-page${n === page ? " is-current" : ""}`}
                        onClick={() => setPage(n)}
                        aria-current={n === page ? "page" : undefined}
                        key={n}
                      >
                        {n}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="ds6-pager-nav"
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page === pages}
                    aria-label="Next page"
                  >
                    <Chevron dir="right" />
                  </button>
                </nav>
              )}
            </footer>
          )}
      </AnchoredPopover>
    </div>
  );
};

PastSchedulesMenu.propTypes = {
  days: PropTypes.array, // [{date, …}] newest first; null while loading
  status: PropTypes.oneOf(["loading", "idle", "error"]),
  onRetry: PropTypes.func,
  date: PropTypes.string.isRequired, // YYYY-MM-DD
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default PastSchedulesMenu;
